// CreditNova Custom Summary Builder Component
// Allows users to configure their credit analysis summary preferences
import { useState, useCallback, memo, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  FileText,
  FileBarChart,
  CreditCard,
  TrendingUp,
  Building2,
  Banknote,
  Save,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type {
  SummaryStyle,
  SummaryLength,
  FinancialStatementType,
  SummaryPreferences,
} from '@creditnova/shared/schemas';
import { useWorkflow } from '@/contexts';

// ===========================================
// Types
// ===========================================

type BankStatementType = 'individual' | 'business';

interface DataPointSelection {
  financialStatements: Set<string>;
  creditReport: Set<string>;
  metrics: Set<string>;
  corporateSearch: Set<string>;
  bankStatements: Set<string>;
}

interface SummaryBuilderProps {
  initialPreferences?: SummaryPreferences | null;
  onSave?: (preferences: SummaryPreferences) => void;
  onCancel?: () => void;
  embedded?: boolean;
}

// ===========================================
// Data Point Constants
// ===========================================

const FINANCIAL_DATA_POINTS = [
  'Total Revenue',
  'Net Income',
  'Total Assets',
  'Total Liabilities',
  'Cash Flow from Operations',
  'Working Capital',
  'All Income Statement Items',
  'All Balance Sheet Items',
  'All Cash Flow Items',
  'Financial Statement Notes',
];

const CREDIT_REPORT_DATA_POINTS = [
  'Credit Score',
  'Payment History',
  'Credit Inquiries',
  'Trade Lines Summary',
  'Public Records',
  'Collections',
  'Total Debt Balance',
  'Credit Utilization',
];

const CORPORATE_SEARCH_DATA_POINTS = [
  'Company Name',
  'Business Number',
  'Registration Date',
  'Business Type',
  'Registered Address',
  'Directors & Officers',
  'Share Structure',
  'Business Activities',
  'All Corporate Information',
];

const INDIVIDUAL_BANK_STATEMENT_DATA_POINTS = [
  'Income Verification',
  'Expense Analysis',
  'Cash Flow Health',
  'Overdraft & NSF Activity',
  'Debt Repayment Behavior',
  'Incoming Transfers',
  'Employer Validation',
  'Seasonality Patterns',
  'Lifestyle Risk Indicators',
  'Asset Verification',
];

const BUSINESS_BANK_STATEMENT_DATA_POINTS = [
  'Revenue (Deposits)',
  'Expense Management (Debits)',
  'Cash Flow Health',
  'Overdrafts & NSFs',
  'Debt Servicing & Loan Repayments',
  'Supplier & Vendor Payments',
  'Seasonality',
  'Tax & Regulatory Remittances',
  'Owner Withdrawals & Related-Party Transfers',
  'Customer Concentration',
  'CapEx Indicators',
  'Internal Transfers & Savings',
  'Payment Behavior & Velocity',
];

// ===========================================
// Memoized Checkbox List Component (Performance Optimization)
// ===========================================

interface DataPointCheckboxListProps {
  points: readonly string[];
  selected: Set<string>;
  category: keyof DataPointSelection;
  idPrefix: string;
  onToggle: (category: keyof DataPointSelection, value: string) => void;
}

const DataPointCheckboxList = memo(function DataPointCheckboxList({
  points,
  selected,
  category,
  idPrefix,
  onToggle,
}: DataPointCheckboxListProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {points.map((point) => (
        <div key={point} className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50">
          <Checkbox
            id={`${idPrefix}-${point}`}
            checked={selected.has(point)}
            onCheckedChange={() => onToggle(category, point)}
          />
          <Label htmlFor={`${idPrefix}-${point}`} className="cursor-pointer flex-1">
            {point}
          </Label>
        </div>
      ))}
    </div>
  );
});

// ===========================================
// SummaryBuilder Component
// ===========================================

export function SummaryBuilder({
  initialPreferences,
  onSave,
  onCancel,
  embedded = false,
}: SummaryBuilderProps) {
  const { toast } = useToast();

  const { data } = useWorkflow();

  useEffect(() => {
    // When data.summaryPreferences changes because of a template load
    if (data.summaryPreferences?.selectedDataPoints) {
      const dp = data.summaryPreferences.selectedDataPoints;

      // Overwrite local checkbox state
      setSelectedDataPoints({
        financialStatements: new Set(dp.financialStatements || []),
        creditReport: new Set(dp.creditReport || []),
        metrics: new Set(dp.metrics || []),
        corporateSearch: new Set(dp.corporateSearch || []),
        bankStatements: new Set(dp.bankStatements || []),
      });

      // Overwrite local style state
      setSummaryStyle(data.summaryPreferences.summaryStyle || 'comprehensive');
      setSummaryLength(data.summaryPreferences.summaryLength || 'standard');
      setCustomInstructions(data.summaryPreferences.customInstructions || '');
      // Sync bank/financial statement type from template
      setBankStatementType((data.summaryPreferences.bankStatementType as BankStatementType) || 'individual');
      setFinancialStatementType(data.summaryPreferences.financialStatementType || 'interim_fs');
      // Sync report name from template if provided
      setReportName(data.summaryPreferences.reportName || '');
    }
  }, [data.summaryPreferences]);

  const [selectedDataPoints, setSelectedDataPoints] = useState<DataPointSelection>(() => {
    const prefs = initialPreferences?.selectedDataPoints;
    return {
      financialStatements: new Set(prefs?.financialStatements || []),
      creditReport: new Set(prefs?.creditReport || []),
      metrics: new Set(prefs?.metrics || []),
      corporateSearch: new Set(prefs?.corporateSearch || []),
      bankStatements: new Set(prefs?.bankStatements || []),
    };
  });

  const [summaryStyle, setSummaryStyle] = useState<SummaryStyle>(
    initialPreferences?.summaryStyle || 'comprehensive'
  );
  const [summaryLength, setSummaryLength] = useState<SummaryLength>(
    initialPreferences?.summaryLength || 'standard'
  );
  const [customInstructions, setCustomInstructions] = useState(
    initialPreferences?.customInstructions || ''
  );
  const [bankStatementType, setBankStatementType] = useState<BankStatementType>(
    (initialPreferences?.bankStatementType as BankStatementType) || 'individual'
  );
  const [financialStatementType, setFinancialStatementType] = useState<FinancialStatementType>(
    initialPreferences?.financialStatementType || 'interim_fs'
  );
  // Optional user-provided report name. Takes priority over names extracted from documents
  // during report generation. If empty, the existing fallback chain is used:
  //   financialData.companyName > creditReport.subjectName > bankStatement.accountHolder > corporate.companyName
  const [reportName, setReportName] = useState(
    initialPreferences?.reportName || ''
  );

  const handleDataPointToggle = useCallback(
    (category: keyof DataPointSelection, value: string) => {
      setSelectedDataPoints((prev) => {
        const newSet = new Set(prev[category]);
        if (newSet.has(value)) {
          newSet.delete(value);
        } else {
          newSet.add(value);
        }
        return { ...prev, [category]: newSet };
      });
    },
    []
  );

  const handleSelectAll = useCallback(
    (category: keyof DataPointSelection, points: readonly string[]) => {
      setSelectedDataPoints((prev) => ({
        ...prev,
        [category]: new Set(points),
      }));
    },
    []
  );

  const handleDeselectAll = useCallback((category: keyof DataPointSelection) => {
    setSelectedDataPoints((prev) => ({
      ...prev,
      [category]: new Set<string>(),
    }));
  }, []);

  const handleBankStatementTypeChange = (type: BankStatementType) => {
    setBankStatementType(type);
    setSelectedDataPoints((prev) => ({
      ...prev,
      bankStatements: new Set<string>(),
    }));
  };

  const currentBankDataPoints =
    bankStatementType === 'individual'
      ? INDIVIDUAL_BANK_STATEMENT_DATA_POINTS
      : BUSINESS_BANK_STATEMENT_DATA_POINTS;

  const { updateData } = useWorkflow(); 

  const syncToWorkflow = (prefs: SummaryPreferences) => {
    updateData({
      summaryPreferences: prefs
    });
  };

  const handleSavePreferences = () => {
    // Convert Sets back to arrays for storage/serialization
    const preferences: SummaryPreferences = {
      selectedDataPoints: {
        financialStatements: Array.from(selectedDataPoints.financialStatements),
        creditReport: Array.from(selectedDataPoints.creditReport),
        metrics: Array.from(selectedDataPoints.metrics),
        corporateSearch: Array.from(selectedDataPoints.corporateSearch),
        bankStatements: Array.from(selectedDataPoints.bankStatements),
      },
      summaryStyle,
      summaryLength,
      customInstructions,
      bankStatementType,
      financialStatementType,
      // Only include reportName if user provided one (non-empty string).
      // When omitted, report generation falls back to extracting the name
      // from document data (financial > credit report > bank > corporate).
      ...(reportName.trim() ? { reportName: reportName.trim() } : {}),
      enableExternalResearch: true,
      createdAt: new Date().toISOString(),
    };

    if (onSave) {
      onSave(preferences);
      syncToWorkflow(preferences);
    } else {
      localStorage.setItem('summaryBuilderPreferences', JSON.stringify(preferences));
    }

    toast({
      title: 'Preferences Saved',
      description: 'Your custom summary preferences have been saved successfully.',
    });
  };

  const totalSelected = Object.values(selectedDataPoints).reduce(
    (acc, set) => acc + set.size,
    0
  );

  const containerClass = embedded
    ? 'space-y-6'
    : 'min-h-screen bg-background py-12';
  const contentClass = embedded ? '' : 'max-w-5xl mx-auto px-8';

  return (
    <div className={containerClass}>
      <div className={contentClass}>
        {/* Header - only show if not embedded */}
        {!embedded && (
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="w-10 h-10 text-primary" />
              <h1 className="text-5xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Custom Summary Builder
              </h1>
            </div>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Design your perfect credit analysis executive summary by selecting specific data
              points from each analysis stage and customizing the tone and format.
            </p>
          </div>
        )}

        {/* Summary Preferences Card */}
        <Card className="p-8 mb-8 border-2 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-primary" />
            Summary Preferences
          </h2>

          <div className="space-y-8">
            {/* Style Selection */}
            <div>
              <Label className="text-lg font-semibold mb-4 block">Summary Style</Label>
              <RadioGroup
                value={summaryStyle}
                onValueChange={(v) => setSummaryStyle(v as SummaryStyle)}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-4 border-2 rounded-xl hover:border-primary/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="comprehensive" id="comprehensive" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="comprehensive" className="text-base font-semibold cursor-pointer">
                          Comprehensive
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Full narrative with detailed analysis and context
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 border-2 rounded-xl hover:border-primary/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="concise" id="concise" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="concise" className="text-base font-semibold cursor-pointer">
                          Concise
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Brief overview focusing on key findings
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 border-2 rounded-xl hover:border-primary/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="bullet-points" id="bullet-points" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="bullet-points" className="text-base font-semibold cursor-pointer">
                          Bullet Points
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Structured bullet-point format for quick scanning
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 border-2 rounded-xl hover:border-primary/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <RadioGroupItem value="executive" id="executive" className="mt-1" />
                      <div className="flex-1">
                        <Label htmlFor="executive" className="text-base font-semibold cursor-pointer">
                          Executive
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          High-level strategic overview for decision-makers
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </RadioGroup>
            </div>

            {/* Length Selection */}
            <div>
              <Label className="text-lg font-semibold mb-4 block">Summary Length</Label>
              <RadioGroup
                value={summaryLength}
                onValueChange={(v) => setSummaryLength(v as SummaryLength)}
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { value: 'brief', label: 'Brief', desc: '~150 words' },
                    { value: 'standard', label: 'Standard', desc: '~300 words' },
                    { value: 'detailed', label: 'Detailed', desc: '~500 words' },
                    { value: 'extensive', label: 'Extensive', desc: '~800+ words' },
                  ].map((option) => (
                    <Card key={option.value} className="p-4 border-2 rounded-xl hover:border-primary/50 transition-colors">
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor={option.value} className="text-base font-semibold cursor-pointer">
                            {option.label}
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">{option.desc}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </RadioGroup>
            </div>

            {/* Report Name — optional, overrides extracted name during report generation */}
            <div>
              <Label htmlFor="report-name" className="text-lg font-semibold mb-3 block">
                Report Name (Optional)
              </Label>
              <Input
                id="report-name"
                placeholder="E.g., 'ABC Corp Q4 2025 Credit Analysis'"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                className="border-2 rounded-xl"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Name for your report and PDF export. If left blank, the name will be
                automatically extracted from the uploaded documents (company name, subject
                name, etc.).
              </p>
            </div>

            {/* Custom Instructions */}
            <div>
              <Label htmlFor="custom-instructions" className="text-lg font-semibold mb-3 block">
                Custom Instructions (Optional)
              </Label>
              <Textarea
                id="custom-instructions"
                placeholder="E.g., 'Focus on risk factors and red flags', 'Emphasize growth potential', 'Compare to industry benchmarks'..."
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                className="min-h-[120px] border-2 rounded-xl"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Provide additional context or specific requirements for your summary generation.
              </p>
            </div>

          </div>
        </Card>

        {/* Data Point Selection */}
        <Card className="p-8 border-2 rounded-2xl shadow-lg">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-2">Select Data Points to Include</h2>
            <p className="text-muted-foreground">
              Choose specific data points from each analysis stage to feature in your summary.{' '}
              <span className="font-semibold text-primary">{totalSelected} items selected</span>
            </p>
          </div>

          <Accordion type="multiple" className="space-y-4">
            {/* 1. Financial Statements */}
            <AccordionItem value="financial" className="border-2 rounded-2xl px-6">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-primary" />
                  <div className="text-left">
                    <div className="font-bold text-lg">Financial Statements</div>
                    <div className="text-sm text-muted-foreground">
                      {selectedDataPoints.financialStatements.size} selected
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                {/* Financial Statement Type Selector */}
                <div className="mb-6 p-4 bg-accent/30 rounded-xl border-2">
                  <Label className="text-base font-semibold mb-3 block">
                    Financial Statement Type
                  </Label>
                  <RadioGroup
                    value={financialStatementType}
                    onValueChange={(v) => setFinancialStatementType(v as FinancialStatementType)}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Card 
                        className={`p-3 border-2 rounded-xl transition-all cursor-pointer ${
                          financialStatementType === 'interim_fs' 
                            ? 'border-primary bg-background shadow-sm' 
                            : 'border-transparent bg-background/50 hover:border-primary/30 hover:bg-background/80'
                        }`}
                        onClick={() => setFinancialStatementType('interim_fs')}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem value="interim_fs" id="interim-fs" className="mt-1" />
                          <div className="flex-1">
                            <Label htmlFor="interim-fs" className="text-sm font-semibold cursor-pointer">
                              Interim
                            </Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Internal financial statements for interim periods
                            </p>
                          </div>
                        </div>
                      </Card>

                      <Card 
                        className={`p-3 border-2 rounded-xl transition-all cursor-pointer ${
                          financialStatementType === 'fye_accountant' 
                            ? 'border-primary bg-background shadow-sm' 
                            : 'border-transparent bg-background/50 hover:border-primary/30 hover:bg-background/80'
                        }`}
                        onClick={() => setFinancialStatementType('fye_accountant')}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem value="fye_accountant" id="fye-accountant" className="mt-1" />
                          <div className="flex-1">
                            <Label htmlFor="fye-accountant" className="text-sm font-semibold cursor-pointer">
                              Accountant Prepared
                            </Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Year-end statements prepared by accountant
                            </p>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </RadioGroup>
                </div>

                <div className="flex gap-2 mb-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSelectAll('financialStatements', FINANCIAL_DATA_POINTS)}
                  >
                    Select All
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeselectAll('financialStatements')}
                  >
                    Deselect All
                  </Button>
                </div>
                <DataPointCheckboxList
                  points={FINANCIAL_DATA_POINTS}
                  selected={selectedDataPoints.financialStatements}
                  category="financialStatements"
                  idPrefix="financial"
                  onToggle={handleDataPointToggle}
                />
              </AccordionContent>
            </AccordionItem>

            {/* 2. Credit Report */}
            <AccordionItem value="credit-report" className="border-2 rounded-2xl px-6">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-6 h-6 text-primary" />
                  <div className="text-left">
                    <div className="font-bold text-lg">Credit Report</div>
                    <div className="text-sm text-muted-foreground">
                      {selectedDataPoints.creditReport.size} selected
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div className="flex gap-2 mb-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSelectAll('creditReport', CREDIT_REPORT_DATA_POINTS)}
                  >
                    Select All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDeselectAll('creditReport')}>
                    Deselect All
                  </Button>
                </div>
                <DataPointCheckboxList
                  points={CREDIT_REPORT_DATA_POINTS}
                  selected={selectedDataPoints.creditReport}
                  category="creditReport"
                  idPrefix="credit-report"
                  onToggle={handleDataPointToggle}
                />
              </AccordionContent>
            </AccordionItem>

            {/* 3. Bank Statement Analysis */}
            <AccordionItem value="bank" className="border-2 rounded-2xl px-6">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <Banknote className="w-6 h-6 text-primary" />
                  <div className="text-left">
                    <div className="font-bold text-lg">Bank Statement Analysis</div>
                    <div className="text-sm text-muted-foreground">
                      {selectedDataPoints.bankStatements.size} selected •{' '}
                      {bankStatementType === 'individual' ? 'Individual' : 'Business'}
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                {/* Bank Statement Type Selector */}
                <div className="mb-6 p-4 bg-accent/20 rounded-xl border-2 border-primary/20">
                  <Label className="text-base font-semibold mb-3 block">
                    Select Bank Statement Type
                  </Label>
                  <RadioGroup
                    value={bankStatementType}
                    onValueChange={(v) => handleBankStatementTypeChange(v as BankStatementType)}
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <Card
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                          bankStatementType === 'individual'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => handleBankStatementTypeChange('individual')}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem value="individual" className="mt-1" />
                          <div className="flex-1">
                            <div className="text-base font-semibold">Individual</div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Personal bank accounts
                            </p>
                          </div>
                        </div>
                      </Card>

                      <Card
                        className={`p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                          bankStatementType === 'business'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => handleBankStatementTypeChange('business')}
                      >
                        <div className="flex items-start gap-3">
                          <RadioGroupItem value="business" className="mt-1" />
                          <div className="flex-1">
                            <div className="text-base font-semibold">Business</div>
                            <p className="text-sm text-muted-foreground mt-1">
                              Business bank accounts
                            </p>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </RadioGroup>
                </div>

                <div className="flex gap-2 mb-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSelectAll('bankStatements', currentBankDataPoints)}
                  >
                    Select All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDeselectAll('bankStatements')}>
                    Deselect All
                  </Button>
                </div>
                <DataPointCheckboxList
                  points={currentBankDataPoints}
                  selected={selectedDataPoints.bankStatements}
                  category="bankStatements"
                  idPrefix="bank"
                  onToggle={handleDataPointToggle}
                />
              </AccordionContent>
            </AccordionItem>

            {/* 4. Corporate Search */}
            <AccordionItem value="corporate" className="border-2 rounded-2xl px-6">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3">
                  <Building2 className="w-6 h-6 text-primary" />
                  <div className="text-left">
                    <div className="font-bold text-lg">Corporate Search</div>
                    <div className="text-sm text-muted-foreground">
                      {selectedDataPoints.corporateSearch.size} selected
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <div className="flex gap-2 mb-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSelectAll('corporateSearch', CORPORATE_SEARCH_DATA_POINTS)}
                  >
                    Select All
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDeselectAll('corporateSearch')}>
                    Deselect All
                  </Button>
                </div>
                <DataPointCheckboxList
                  points={CORPORATE_SEARCH_DATA_POINTS}
                  selected={selectedDataPoints.corporateSearch}
                  category="corporateSearch"
                  idPrefix="corporate"
                  onToggle={handleDataPointToggle}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>

        {/* Document Types Required — computed from selected data points.
            Shows the user which document types they will need to upload based on
            their current data point selections. Each indicator maps directly to a
            drop zone on the upcoming unified upload page. */}
        {totalSelected > 0 && (
          <Card className="p-6 mt-8 border-2 rounded-2xl bg-accent/5">
            <h3 className="text-lg font-semibold mb-4">Documents You Will Need to Upload</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Financial Statements — required when any financial or metrics data points are selected */}
              {(selectedDataPoints.financialStatements.size > 0 || selectedDataPoints.metrics.size > 0) && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-background border">
                  <FileBarChart className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium">Financial Statements</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" />
                </div>
              )}
              {/* Credit Report — required when any credit report data points are selected */}
              {(selectedDataPoints.creditReport.size > 0) && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-background border">
                  <CreditCard className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium">Credit Report</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" />
                </div>
              )}
              {/* Bank Statements — required when any bank statement data points are selected */}
              {(selectedDataPoints.bankStatements.size > 0) && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-background border">
                  <Banknote className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium">Bank Statements</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" />
                </div>
              )}
              {/* Corporate Search — required when any corporate data points are selected */}
              {(selectedDataPoints.corporateSearch.size > 0) && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-background border">
                  <Building2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm font-medium">Corporate Search</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" />
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          {onCancel && (
            <Button variant="outline" size="lg" onClick={onCancel} className="px-8 py-6 text-lg rounded-2xl">
              Cancel
            </Button>
          )}
          <Button
            size="lg"
            onClick={handleSavePreferences}
            className="px-8 py-6 text-lg rounded-2xl shadow-lg border-2"
          >
            <Save className="w-5 h-5 mr-2" />
            Save Custom Summary Preferences
          </Button>
        </div>

        {/* Info Card */}
        {!embedded && (
          <Card className="p-6 mt-8 border-2 rounded-2xl bg-primary/5">
            <p className="text-center text-muted-foreground">
              <strong>Next Step:</strong> After saving your preferences, proceed to generate your
              credit analysis report. The AI will create a custom executive summary based on your
              selections and preferences, featuring only the data points you've chosen in your
              preferred style and length.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

